/**
 * career-werte-sektion — the culminating Werte-Sektion (W1 redesign §5b–§8, V5),
 * route `/workshops/values/sektion`.
 *
 * The statements pendant that closes Workshop 1: from the committed pyramid + the
 * interview »Signale« we DRAFT the per-value detail (top-4) and the four statements
 * (»Mein Warum« / »Meine Positionierung« / »Meine Arbeitsphilosophie« / »Meine
 * Vision«), then the user CONFIRMS/EDITS before anything is saved (HITL — the draft is
 * never silently persisted). Honest-gap discipline: a statement the evidence can't carry
 * says so.
 *
 * The surface reads as a designed booklet page — a single centered document column:
 * »Mein Warum« as a hero pull-quote, the values as a scannable expandable list, the
 * remaining statements as labelled prose. Every block is VIEW-FIRST: it renders as prose
 * with a hover-revealed edit + Feedback affordance. Editing swaps the block to a full-text
 * editor; »Feedback« opens an inline composer that asks the AI seam for a proposal the user
 * accepts EXPLICITLY (HITL — the original is never silently replaced). In review mode the
 * same layout carries an »Entwurf« chip + a sticky confirm bar; in saved mode a block
 * accept persists immediately via the confirm endpoint (the full edited section).
 * Candidate-facing, elicited material is »Signale«, never »Belege«.
 *
 * PUSH-FIRST: the surface claims the self-knowledge workshop stream and re-reads the
 * server view when it moves, so a synthesis produced elsewhere (the other tab, or the
 * request of ours that died on the way back) arrives here live. The re-read is
 * DEFERRED while a review is open — that state is the user's own unconfirmed work and
 * belongs to the local draft, never to a background event.
 */

import {
  KitElement,
  signal,
  type ActionMap,
  type KitContext,
  type PushChannel,
  type ReadonlySignal,
  type Router,
  type Signal,
  type ViewModel,
  type Urn,
} from "@platform/web-kit"
import { classMap, html, nothing, type TemplateResult } from "@platform/web-kit/render"
import { seamAutoGrow } from "@platform/rlhf-elements"
import type { PersonalStatement, PersonalStatements, PersonalValueEntry } from "@career/definition-v2"
import { I18nToken, PushToken, RouterToken, SessionToken, WorkshopStoreToken, apiGet, apiPost, isSessionExpired } from "../services"
import { createSektionDraftStore, type SektionDraft, type SektionDraftStore } from "../lib/sektion-draft"
import { careerStream, workshopUrn, WORKSHOP_AGGREGATE, type ShellSession, type WorkshopStore } from "../domain"
import { acquireStreams, onChange } from "../lib/push-streams"
import type { CareerI18n, CareerKey } from "../../i18n"
import { nextStep } from "../lib/next-step"
import { spineDecl, toNextStep } from "../golden-path"

interface SektionView {
  values: PersonalValueEntry[]
  statements: PersonalStatements | null
  produced: boolean
  run: unknown
}
interface InterviewLite {
  pyramid: unknown | null
  complete: boolean
}
interface SynthesizeOutput {
  values: PersonalValueEntry[]
  statements: PersonalStatements
  run: unknown
}
interface ConfirmOutput {
  section: SektionView
}
interface RefineOutput {
  proposal: string
  run: unknown
}
/** The resume-by-read: the latest captured synthesis, re-reconciled server-side. */
interface SektionDraftOutput {
  draft: WorkingSection | null
  createdAt: string | null
}

/**
 * How long we keep looking for a synthesis whose HTTP response died in flight. iOS Safari
 * kills a long fetch (and backgrounding the tab kills it outright) while the server happily
 * finishes the call, captures the run and debits the credits — so the deliverable exists and
 * is already paid for. Six tries at 2.5 s covers the gap between the client giving up and the
 * model finishing without holding the user in a spinner for long.
 */
const RESUME_POLL_ATTEMPTS = 6
const RESUME_POLL_DELAY_MS = 2500

const delay = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms))

/** The editable working model — the review draft OR the saved section, one shape. */
interface WorkingSection {
  values: PersonalValueEntry[]
  statements: PersonalStatements
}

type StatementKind = PersonalStatement["kind"]
/** The refine target — a statement (by kind) or a value field (by cardId). */
type RefineField = "definition" | "leitsatz"
type RefineTarget =
  | { kind: "statement"; statement: StatementKind }
  | { kind: "value"; cardId: string; field: RefineField }

/** The statement kinds, always in their canonical order (Q3 — all four attempted). */
const STATEMENT_ORDER: StatementKind[] = ["warum", "positionierung", "philosophie", "vision"]
/** The three statements that live in »Auf einen Blick« (»warum« is the hero). */
const GLANCE_ORDER: StatementKind[] = ["positionierung", "philosophie", "vision"]

const STATEMENT_LABEL: Record<StatementKind, CareerKey> = {
  warum: "sektion.statement.warum",
  positionierung: "sektion.statement.positionierung",
  philosophie: "sektion.statement.philosophie",
  vision: "sektion.statement.vision",
}

/** A stable block key: `stmt:<kind>` or `val:<cardId>:<field>` (cardId is a colon-free slug). */
function statementBlock(kind: StatementKind): string {
  return `stmt:${kind}`
}
function valueBlock(cardId: string, field: RefineField): string {
  return `val:${cardId}:${field}`
}
/** Parse a block key back into a refine target (the wire shape). */
function parseBlock(id: string): RefineTarget {
  if (id.startsWith("stmt:")) return { kind: "statement", statement: id.slice(5) as StatementKind }
  const rest = id.slice(4) // drop "val:"
  const sep = rest.lastIndexOf(":")
  return { kind: "value", cardId: rest.slice(0, sep), field: rest.slice(sep + 1) as RefineField }
}

/** The top values, Lebenswert first then Superkräfte (Basiswerte are not detailed — Q5). */
function topValues(values: PersonalValueEntry[]): PersonalValueEntry[] {
  const rank = (t: string): number => (t === "Lebenswert" ? 0 : 1)
  return values.filter((v) => v.tier !== "Basiswerte").sort((a, b) => rank(a.tier) - rank(b.tier))
}

/** The current text of a block within the working section. */
function textOf(section: WorkingSection, id: string): string {
  const t = parseBlock(id)
  if (t.kind === "statement") return section.statements.statements.find((s) => s.kind === t.statement)?.text ?? ""
  const v = section.values.find((e) => e.cardId === t.cardId)
  return (t.field === "definition" ? v?.definition : v?.leitsatz) ?? ""
}

/** Return a NEW working section with `id`'s text replaced (an edit may only touch TEXT). */
function withText(section: WorkingSection, id: string, text: string): WorkingSection {
  const t = parseBlock(id)
  if (t.kind === "statement") {
    const statements = section.statements.statements.map((s) => (s.kind === t.statement ? { ...s, text } : s))
    return { ...section, statements: { ...section.statements, statements } }
  }
  const values = section.values.map((v) => (v.cardId === t.cardId ? { ...v, [t.field]: text } : v))
  return { ...section, values }
}

export class WerteSektionVm implements ViewModel {
  readonly view: Signal<SektionView | null> = signal<SektionView | null>(null)
  readonly interview: Signal<InterviewLite | null> = signal<InterviewLite | null>(null)
  /** The editable working section — set in BOTH review (draft) and saved mode. */
  readonly working: Signal<WorkingSection | null> = signal<WorkingSection | null>(null)
  /** True while a synthesis draft is under HITL review (pre-confirm). */
  readonly reviewing: Signal<boolean> = signal(false)
  readonly loaded: Signal<boolean> = signal(false)
  readonly error: Signal<string | null> = signal<string | null>(null)
  readonly busy: Signal<boolean> = signal(false)
  readonly saved: Signal<boolean> = signal(false)
  /** The block currently in edit-in-place (null = none); single active editor. */
  readonly editing: Signal<string | null> = signal<string | null>(null)
  /** The block whose Feedback composer is open (null = none). */
  readonly refineOpen: Signal<string | null> = signal<string | null>(null)
  readonly refineProposal: Signal<string | null> = signal<string | null>(null)
  readonly refineBusy: Signal<boolean> = signal(false)
  /** The expanded value rows (by cardId). */
  readonly expanded: Signal<ReadonlySet<string>> = signal<ReadonlySet<string>>(new Set())
  /** A captured synthesis found server-side that the user may restore (null = none offered). */
  readonly serverDraft: Signal<WorkingSection | null> = signal<WorkingSection | null>(null)
  /** True while we're waiting on a synthesis whose response was lost in flight. */
  readonly resuming: Signal<boolean> = signal(false)
  readonly renderDeps: ReadonlyArray<ReadonlySignal<unknown>>
  readonly t: CareerI18n["t"]

  /** Per-user local draft — the review's insurance against a reload / session-expiry re-auth. */
  private readonly draft: SektionDraftStore

  constructor(
    private readonly router: Router,
    i18n: CareerI18n,
    private readonly session: ShellSession,
    private readonly push: PushChannel,
    private readonly store: WorkshopStore,
  ) {
    this.t = i18n.t
    this.draft = createSektionDraftStore(session.user?.sub ?? null)
    this.renderDeps = [
      this.view,
      this.interview,
      this.working,
      this.reviewing,
      this.loaded,
      this.error,
      this.busy,
      this.saved,
      this.editing,
      this.refineOpen,
      this.refineProposal,
      this.refineBusy,
      this.expanded,
      this.serverDraft,
      this.resuming,
    ]
  }

  attach(): () => void {
    void this.refresh()
    const userId = this.session.user?.sub
    if (!userId) return () => {}
    const release = acquireStreams(this.push, [careerStream(userId, WORKSHOP_AGGREGATE, workshopUrn(userId, "self_knowledge"))])
    const stop = onChange(this.store.entity(workshopUrn(userId, "self_knowledge")), () => this.pushRefresh())
    return () => {
      stop()
      release()
    }
  }

  /**
   * The push-driven re-read. It stands DOWN while a review is open or a call is in
   * flight: an unconfirmed review holds edits that exist nowhere but here and in the
   * local draft, and re-entering it from a background event would reset the reader's
   * place (and could fight a synthesis that is still landing).
   */
  private pushRefresh(): void {
    if (this.reviewing.value || this.busy.value || this.refineBusy.value) return
    void this.refresh()
  }

  private async refresh(): Promise<void> {
    // Read the LOCAL draft FIRST — it must be able to rescue an in-flight review even when the
    // server read fails or the session just expired. A draft only ever exists while a review is
    // unconfirmed (`persist()` clears it on success), so its presence outranks the produced
    // view: it holds the user's edits, which live nowhere else.
    const local = this.draft.load()
    try {
      const [view, interview] = await Promise.all([
        apiGet<SektionView>("/api/workshops/values/sektion"),
        apiGet<InterviewLite>("/api/workshops/values/interview"),
      ])
      this.view.set(view)
      this.interview.set(interview)
      if (local) this.enterReview(local)
      else if (view.produced) this.working.set(sectionFrom(view))
      else if (interview.pyramid) await this.offerServerDraft()
    } catch (e) {
      // Offline / session expired: the server view is gone, but a local draft still rescues the
      // review — restore it. A 401 is owned by the shell's re-auth prompt, so never ALSO show a
      // raw surface error for it; other failures with no draft to fall back on do.
      if (local) this.enterReview(local)
      if (!local && !isSessionExpired(e)) this.error.set(e instanceof Error ? e.message : String(e))
    } finally {
      this.loaded.set(true)
    }
  }

  /** Enter HITL review from a section — the state a fresh synthesis leaves behind. */
  private enterReview(section: WorkingSection): void {
    this.working.set({ values: section.values, statements: section.statements })
    this.reviewing.set(true)
    this.serverDraft.set(null)
    // Open the top values so the reviewer sees every detail to confirm.
    this.expanded.set(new Set(topValues(section.values).map((v) => v.cardId)))
  }

  /** Mirror the working section into the local draft — called on EVERY review mutation. */
  private saveDraft(): void {
    const section = this.working.value
    if (!section || !this.reviewing.value) return
    this.draft.save({ ...section, updatedAt: new Date().toISOString() })
  }

  /**
   * Read the latest captured synthesis (paid for, re-reconciled server-side). `since` accepts
   * only a run from the current attempt, so a lost response never resurrects an OLDER draft and
   * presents it as the new one.
   */
  private async readServerDraft(since?: string): Promise<WorkingSection | null> {
    try {
      const res = await apiGet<SektionDraftOutput>("/api/workshops/values/sektion/draft")
      if (!res.draft) return null
      if (since !== undefined && (res.createdAt === null || res.createdAt < since)) return null
      return res.draft
    } catch {
      return null // the resume is a bonus path — never let it produce an error of its own
    }
  }

  /** Offer a previously synthesized section the user never got to see (no local draft). */
  private async offerServerDraft(): Promise<void> {
    this.serverDraft.set(await this.readServerDraft())
  }

  /** Take up the offered server draft — from here it is a normal review, backed up locally. */
  restoreServerDraft(): void {
    const section = this.serverDraft.value
    if (!section) return
    this.enterReview(section)
    this.saveDraft()
  }

  async synthesize(): Promise<void> {
    if (this.busy.value) return
    const startedAt = new Date().toISOString()
    this.busy.set(true)
    this.error.set(null)
    this.saved.set(false)
    this.serverDraft.set(null)
    this.resetBlockState()
    try {
      const res = await apiPost<SynthesizeOutput>("/api/workshops/values/sektion/synthesize")
      this.enterReview({ values: res.values, statements: res.statements })
      this.saveDraft()
    } catch (e) {
      // The synthesis may well have SUCCEEDED and only the response died in flight — the run is
      // captured and the credits are spent either way. Look for the artifact before reporting a
      // failure, so the user gets what they paid for instead of paying twice.
      const rescued = await this.pollForDraft(startedAt)
      if (rescued) {
        this.enterReview(rescued)
        this.saveDraft()
      } else if (!isSessionExpired(e)) {
        this.error.set(e instanceof Error ? e.message : String(e))
      }
    } finally {
      this.resuming.set(false)
      this.busy.set(false)
    }
  }

  /** Poll the draft read for a bounded window after a lost synthesis response. */
  private async pollForDraft(since: string): Promise<WorkingSection | null> {
    this.resuming.set(true)
    for (let attempt = 0; attempt < RESUME_POLL_ATTEMPTS; attempt++) {
      await delay(RESUME_POLL_DELAY_MS)
      const found = await this.readServerDraft(since)
      if (found) return found
    }
    return null
  }

  /** Persist the (edited) working section — the HITL confirm (review) or an inline save (saved). */
  async persist(): Promise<void> {
    const section = this.working.value
    if (this.busy.value || !section) return
    this.busy.set(true)
    this.error.set(null)
    try {
      const res = await apiPost<ConfirmOutput>("/api/workshops/values/sektion/confirm", section)
      this.view.set(res.section)
      this.working.set(sectionFrom(res.section))
      // The section is durable server-side now — the local rescue copy has done its job.
      this.draft.clear()
      if (this.reviewing.value) {
        this.reviewing.set(false)
        this.saved.set(true)
      }
      this.resetBlockState()
    } catch (e) {
      this.error.set(e instanceof Error ? e.message : String(e))
    } finally {
      this.busy.set(false)
    }
  }

  /** Apply an edited/accepted text to a block; persist immediately in saved mode. */
  private applyText(id: string, text: string): void {
    const section = this.working.value
    if (!section) return
    this.working.set(withText(section, id, text.trim()))
    // Mid-review this edit exists NOWHERE else until confirm — back it up before anything can
    // end the page's life (a reload, or the 401 route-to-login round-trip).
    this.saveDraft()
    this.resetBlockState()
    if (!this.reviewing.value) void this.persist()
  }

  saveEdit(id: string, text: string): void {
    this.applyText(id, text)
  }

  cancelEdit(): void {
    this.editing.set(null)
  }

  startEdit(id: string): void {
    this.editing.set(id)
    this.refineOpen.set(null)
    this.refineProposal.set(null)
  }

  openRefine(id: string): void {
    this.refineOpen.set(id)
    this.refineProposal.set(null)
    this.editing.set(null)
  }

  cancelRefine(): void {
    this.refineOpen.set(null)
    this.refineProposal.set(null)
  }

  retryRefine(): void {
    this.refineProposal.set(null)
  }

  async submitRefine(id: string, feedback: string): Promise<void> {
    const section = this.working.value
    if (this.refineBusy.value || !section || feedback.trim().length === 0) return
    this.refineBusy.set(true)
    this.error.set(null)
    try {
      const res = await apiPost<RefineOutput>("/api/workshops/values/sektion/refine", {
        target: parseBlock(id),
        currentText: textOf(section, id),
        feedback: feedback.trim(),
      })
      this.refineProposal.set(res.proposal)
    } catch (e) {
      this.error.set(e instanceof Error ? e.message : String(e))
    } finally {
      this.refineBusy.set(false)
    }
  }

  acceptRefine(id: string): void {
    const proposal = this.refineProposal.value
    if (proposal === null) return
    this.applyText(id, proposal)
  }

  toggleExpand(cardId: string): void {
    const next = new Set(this.expanded.value)
    if (next.has(cardId)) next.delete(cardId)
    else next.add(cardId)
    this.expanded.set(next)
  }

  private resetBlockState(): void {
    this.editing.set(null)
    this.refineOpen.set(null)
    this.refineProposal.set(null)
  }

  go(url: string): void {
    void this.router.navigate(url)
  }
}

/** Derive the editable working section from a read view (saved mode). */
function sectionFrom(view: SektionView): WorkingSection {
  return { values: view.values, statements: view.statements ?? { statements: [] } }
}

const ICON = {
  pencil: html`<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" /></svg>`,
  spark: html`<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3v4M12 17v4M3 12h4M17 12h4M6 6l2.5 2.5M15.5 15.5 18 18M18 6l-2.5 2.5M8.5 15.5 6 18" /></svg>`,
  chevron: html`<svg class="ws-vchevron" viewBox="0 0 24 24" aria-hidden="true"><path d="m9 6 6 6-6 6" /></svg>`,
}

export class CareerWerteSektion extends KitElement<WerteSektionVm> {
  protected createViewModel(ctx: KitContext): WerteSektionVm {
    return new WerteSektionVm(
      ctx.inject(RouterToken),
      ctx.inject(I18nToken),
      ctx.inject(SessionToken),
      ctx.inject(PushToken),
      ctx.inject(WorkshopStoreToken),
    )
  }

  // ── One editable block: prose ⇄ editor, with the Feedback composer + proposal ──

  private block(vm: WerteSektionVm, id: string, text: string, opts: { proseClass: string; gapClass?: string }): TemplateResult {
    const editing = vm.editing.value === id
    if (editing) return this.editor(vm, id, text)

    const isGap = text.trim().length === 0
    const proseClass = classMap({ [opts.proseClass]: true, "is-gap": isGap })
    const prose = isGap
      ? html`<p class=${proseClass} lang="de">${vm.t("sektion.gap")}</p>`
      : html`<p class=${proseClass} lang="de">${text}</p>`

    return html`<div class="ws-block" data-block=${id}>
      ${prose}
      <div class="ws-afford">
        <button class="ws-iconbtn" data-act="edit-start" data-block=${id} lang="de" aria-label=${vm.t("sektion.edit.aria")}>
          ${ICON.pencil}<span>${vm.t("sektion.edit")}</span>
        </button>
        <button class="ws-iconbtn" data-act="refine-open" data-block=${id} lang="de" aria-label=${vm.t("sektion.refine.aria")}>
          ${ICON.spark}<span>${vm.t("sektion.refine.open")}</span>
        </button>
      </div>
      ${vm.refineOpen.value === id ? this.refine(vm, id) : nothing}
    </div>`
  }

  private editor(vm: WerteSektionVm, id: string, text: string): TemplateResult {
    const rows = Math.min(12, Math.max(2, text.split("\n").length + Math.ceil(text.length / 64)))
    return html`<div class="ws-block is-editing" data-block=${id}>
      <div class="ws-editor">
        <textarea class="ws-textarea ws-editor-input" rows=${rows} lang="de" ${seamAutoGrow()} .value=${text}></textarea>
        <div class="ws-editor-actions">
          <button class="ws-btn-sm is-primary" data-act="edit-save" data-block=${id} ?disabled=${vm.busy.value} lang="de">${vm.t("sektion.edit.save")}</button>
          <button class="ws-btn-sm" data-act="edit-cancel" lang="de">${vm.t("sektion.edit.cancel")}</button>
        </div>
      </div>
    </div>`
  }

  private refine(vm: WerteSektionVm, id: string): TemplateResult {
    const proposal = vm.refineProposal.value
    return html`<div class="ws-refine" data-block=${id}>
      ${proposal === null
        ? html`
            <p class="ws-refine-label" lang="de">${vm.t("sektion.refine.aria")}</p>
            <textarea class="ws-refine-input" rows="2" lang="de" placeholder=${vm.t("sektion.refine.placeholder")} ${seamAutoGrow()}></textarea>
            <div class="ws-refine-actions">
              <button
                class=${classMap({ "ws-btn-sm": true, "is-primary": true, "is-pending": vm.refineBusy.value })}
                data-act="refine-submit" data-block=${id} ?disabled=${vm.refineBusy.value} lang="de"
              >${vm.refineBusy.value ? vm.t("sektion.refine.submitting") : vm.t("sektion.refine.submit")}</button>
              <button class="ws-btn-sm" data-act="refine-cancel" lang="de">${vm.t("sektion.refine.cancel")}</button>
            </div>`
        : html`
            <div class="ws-proposal">
              <p class="ws-proposal-label" lang="de">${vm.t("sektion.proposal.label")}</p>
              <p class="ws-proposal-text" lang="de">${proposal}</p>
              <div class="ws-proposal-actions">
                <button class="ws-btn-sm is-primary" data-act="refine-accept" data-block=${id} ?disabled=${vm.busy.value} lang="de">${vm.t("sektion.proposal.accept")}</button>
                <button class="ws-btn-sm" data-act="refine-again" lang="de">${vm.t("sektion.proposal.again")}</button>
                <button class="ws-btn-sm" data-act="refine-cancel" lang="de">${vm.t("sektion.proposal.discard")}</button>
              </div>
            </div>`}
    </div>`
  }

  // ── The document ───────────────────────────────────────────────────────────────

  private document(vm: WerteSektionVm, section: WorkingSection): TemplateResult {
    const reviewing = vm.reviewing.value
    const byKind = new Map(section.statements.statements.map((s) => [s.kind, s.text]))
    const top = topValues(section.values)
    const warum = byKind.get("warum") ?? ""

    return html`
      <div class="ws-doc">
        ${reviewing
          ? html`<p class="ws-draft-note" lang="de"><span class="chip is-draft">${vm.t("sektion.review.badge")}</span>${vm.t("sektion.review.note")}</p>`
          : vm.saved.value
            ? html`<p class="ws-saved-note" role="status" lang="de">${vm.t("sektion.saved")}</p>`
            : nothing}

        <!-- Hero: »Mein Warum« as the pull-quote -->
        <section class="ws-hero" aria-labelledby="ws-hero-label">
          <p class="ws-hero-label" id="ws-hero-label" lang="de">${vm.t(STATEMENT_LABEL.warum)}</p>
          ${this.heroBlock(vm, warum)}
        </section>

        <!-- The values, as a scannable expandable list -->
        <section class="ws-section" aria-labelledby="ws-values-label">
          <header class="ws-sec-head">
            <h2 class="ws-sec-label" id="ws-values-label" lang="de">${vm.t("sektion.doc.values")}</h2>
            <span class="ws-sec-count" lang="de">${top.length} ${vm.t("sektion.doc.valuesUnit")}</span>
          </header>
          <div class="ws-vlist">${top.map((v) => this.valueRow(vm, v))}</div>
        </section>

        <!-- »Auf einen Blick«: the remaining statements as labelled prose -->
        <section class="ws-section" aria-labelledby="ws-glance-label">
          <header class="ws-sec-head">
            <h2 class="ws-sec-label" id="ws-glance-label" lang="de">${vm.t("sektion.doc.aufEinenBlick")}</h2>
          </header>
          <div class="ws-prose-list">
            ${GLANCE_ORDER.map(
              (k) => html`<div class="ws-glance">
                <p class="ws-block-label" lang="de">${vm.t(STATEMENT_LABEL[k])}</p>
                ${this.block(vm, statementBlock(k), byKind.get(k) ?? "", { proseClass: "ws-prose" })}
              </div>`,
            )}
          </div>
        </section>

        ${reviewing
          ? html`<div class="ws-confirmbar">
              <button
                class=${classMap({ primary: true, "ws-confirm": true, "is-pending": vm.busy.value })}
                data-act="confirm" ?disabled=${vm.busy.value} lang="de"
              >${vm.busy.value ? vm.t("sektion.confirming") : vm.t("sektion.confirm")}</button>
            </div>`
          : html`<button class=${classMap({ ghost: true, "ws-redraft": true, "is-pending": vm.busy.value })} data-act="synthesize" ?disabled=${vm.busy.value} lang="de">
              ${vm.busy.value ? vm.t("sektion.intro.synthesizing") : vm.t("sektion.resynthesize")}
            </button>`}
      </div>`
  }

  /** The hero »Warum« block — a serif pull-quote in view mode; the shared editor when editing. */
  private heroBlock(vm: WerteSektionVm, text: string): TemplateResult {
    const id = statementBlock("warum")
    if (vm.editing.value === id) return this.editor(vm, id, text)
    const isGap = text.trim().length === 0
    const quoteClass = classMap({ "ws-hero-quote": true, "is-gap": isGap })
    return html`<div class="ws-block" data-block=${id}>
      <p class=${quoteClass} lang="de">${isGap ? vm.t("sektion.gap") : text}</p>
      <div class="ws-afford">
        <button class="ws-iconbtn" data-act="edit-start" data-block=${id} lang="de" aria-label=${vm.t("sektion.edit.aria")}>
          ${ICON.pencil}<span>${vm.t("sektion.edit")}</span>
        </button>
        <button class="ws-iconbtn" data-act="refine-open" data-block=${id} lang="de" aria-label=${vm.t("sektion.refine.aria")}>
          ${ICON.spark}<span>${vm.t("sektion.refine.open")}</span>
        </button>
      </div>
      ${vm.refineOpen.value === id ? this.refine(vm, id) : nothing}
    </div>`
  }

  private valueRow(vm: WerteSektionVm, entry: PersonalValueEntry): TemplateResult {
    const open = vm.expanded.value.has(entry.cardId)
    const isLebenswert = entry.tier === "Lebenswert"
    const rowClass = classMap({ "ws-vrow": true, "is-open": open, "is-lebenswert": isLebenswert })
    const markClass = classMap({ "ws-vmark": true, "is-super": !isLebenswert })
    return html`<div class=${rowClass}>
      <button
        class="ws-vrow-head" data-act="toggle-expand" data-cardid=${entry.cardId}
        aria-expanded=${open ? "true" : "false"} lang="de"
      >
        <span class=${markClass} aria-hidden="true"></span>
        <span class="ws-vname">${entry.name}</span>
        ${entry.leitsatz ? html`<span class="ws-vlead" lang="de">${entry.leitsatz}</span>` : html`<span class="ws-vlead is-gap" lang="de">${vm.t("sektion.doc.expandHint")}</span>`}
        ${ICON.chevron}
      </button>
      ${open
        ? html`<div class="ws-vbody">
            <div class="ws-field">
              <p class="ws-block-label" lang="de">${vm.t("sektion.value.definition")}</p>
              ${this.block(vm, valueBlock(entry.cardId, "definition"), entry.definition ?? "", { proseClass: "ws-prose" })}
            </div>
            <div class="ws-field">
              <p class="ws-block-label" lang="de">${vm.t("sektion.value.leitsatz")}</p>
              ${this.block(vm, valueBlock(entry.cardId, "leitsatz"), entry.leitsatz ?? "", { proseClass: "ws-prose ws-prose-leitsatz" })}
            </div>
          </div>`
        : nothing}
    </div>`
  }

  // ── States ───────────────────────────────────────────────────────────────────

  private needPyramid(vm: WerteSektionVm): TemplateResult {
    return html`<div class="ws-intro">
      <p class="surface-lead" lang="de">${vm.t("sektion.intro.needPyramid")}</p>
      <button class="primary" data-act="go-cardgame" lang="de">${vm.t("sektion.intro.toCardgame")}</button>
    </div>`
  }

  private readyToSynthesize(vm: WerteSektionVm): TemplateResult {
    return html`<div class="ws-intro">
      <p class="surface-lead" lang="de">${vm.t("sektion.intro.lede")}</p>
      ${vm.serverDraft.value
        ? html`<p class="ws-resume" lang="de">
            ${vm.t("sektion.resume.offer")}
            <button class="ws-btn-sm is-primary" data-act="restore-draft" lang="de">${vm.t("sektion.resume.action")}</button>
          </p>`
        : nothing}
      <button class=${classMap({ primary: true, "is-pending": vm.busy.value })} data-act="synthesize" ?disabled=${vm.busy.value} lang="de">
        ${vm.busy.value ? vm.t("sektion.intro.synthesizing") : vm.t("sektion.intro.synthesize")}
      </button>
      ${vm.resuming.value ? html`<p class="ws-resume-wait" role="status" lang="de">${vm.t("sektion.resume.waiting")}</p>` : nothing}
    </div>`
  }

  private body(vm: WerteSektionVm): TemplateResult {
    const section = vm.working.value
    if (vm.reviewing.value && section) return this.document(vm, section) // review mode wins (mid-HITL)
    const interview = vm.interview.value
    if (!interview?.pyramid) return this.needPyramid(vm)
    if (vm.view.value?.produced && section) return this.document(vm, section)
    return this.readyToSynthesize(vm)
  }

  /** The golden-path forward affordance (D4): once the section is confirmed (produced, not
   *  mid-review) → onward to the Kompass; otherwise locked with the honest reason (AX-10). */
  private goldenNext(vm: WerteSektionVm): TemplateResult | typeof nothing {
    const decl = spineDecl("career-werte-sektion")
    if (!decl) return nothing
    const confirmed = Boolean(vm.view.value?.produced) && !vm.reviewing.value
    const reason = confirmed ? undefined : vm.t("next.locked.sektion")
    return nextStep(vm.t("next.eyebrow"), toNextStep(decl, vm.t(decl.labelKey), reason))
  }

  protected render(vm: WerteSektionVm): TemplateResult {
    const rootUrn = (this.urn ?? "urn:career:workshop:values") as Urn
    const err = vm.error.value
    return html`
      <section class="surface werte-sektion" urn=${rootUrn} aria-labelledby="ws-title">
        <header class="ws-head">
          <p class="eyebrow" lang="de">${vm.t("sektion.kicker")}</p>
          <h1 id="ws-title" class="surface-title ws-doc-title" lang="de">${vm.t("surface.werteSektion.title")}</h1>
        </header>
        ${err ? html`<p class="surface-error" role="alert" lang="de">${vm.t("review.error", { message: err })}</p>` : nothing}
        ${vm.loaded.value ? this.body(vm) : nothing}
        ${vm.loaded.value ? this.goldenNext(vm) : nothing}
      </section>`
  }

  protected override actions(vm: WerteSektionVm): ActionMap {
    return {
      "go-cardgame": () => vm.go("/workshops/values"),
      nav: ({ url }, event) => {
        event.preventDefault()
        if (url) vm.go(url)
      },
      synthesize: () => void vm.synthesize(),
      "restore-draft": () => vm.restoreServerDraft(),
      confirm: () => void vm.persist(),
      "toggle-expand": (data) => {
        if (data["cardid"]) vm.toggleExpand(data["cardid"])
      },
      "edit-start": (data) => {
        if (data["block"]) vm.startEdit(data["block"])
      },
      "edit-cancel": () => vm.cancelEdit(),
      "edit-save": (data, event) => {
        const id = data["block"]
        if (id && event) vm.saveEdit(id, this.readBlockValue(event, ".ws-editor-input"))
      },
      "refine-open": (data) => {
        if (data["block"]) vm.openRefine(data["block"])
      },
      "refine-cancel": () => vm.cancelRefine(),
      "refine-again": () => vm.retryRefine(),
      "refine-submit": (data, event) => {
        const id = data["block"]
        if (id && event) void vm.submitRefine(id, this.readBlockValue(event, ".ws-refine-input"))
      },
      "refine-accept": (data) => {
        if (data["block"]) vm.acceptRefine(data["block"])
      },
    }
  }

  /** Read the edited value of the textarea inside the acting block (DOM read — the runner's pattern). */
  private readBlockValue(event: Event, sel: string): string {
    const block = (event.target as HTMLElement).closest(".ws-block")
    return block?.querySelector<HTMLTextAreaElement>(sel)?.value ?? ""
  }
}
